/**
 * ConfirmDialog — the prompt in front of anything that displaces a requestor (OPEN-053).
 *
 * Driven entirely by `confirmationFor` in `slot-edit.ts`: pass its result, or `null` to stay
 * closed. Every entry point (slot picker, right-click menu, drag, bulk wizard) renders THIS,
 * so none of them can ship without the guard by accident.
 */
import * as React from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from 'src/components/ui/dialog';
import { Button } from 'src/components/ui/button';
import type { ConfirmPrompt } from './slot-edit';

export function ConfirmDialog({
  prompt,
  pending,
  onCancel,
  onConfirm,
}: {
  readonly prompt: ConfirmPrompt | null;
  readonly pending: boolean;
  readonly onCancel: () => void;
  readonly onConfirm: () => void;
}): React.JSX.Element {
  return (
    <Dialog
      open={prompt !== null}
      onOpenChange={(open): void => {
        if (!open) onCancel();
      }}
    >
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{prompt?.title ?? ''}</DialogTitle>
        </DialogHeader>
        <p style={{ fontSize: '14px', color: 'var(--color-text-secondary)', lineHeight: 1.5 }}>
          {prompt?.body ?? ''}
        </p>
        <DialogFooter>
          <Button type="button" variant="outline" onClick={onCancel} disabled={pending}>
            Cancel
          </Button>
          <Button
            type="button"
            disabled={pending}
            onClick={onConfirm}
            style={
              prompt?.destructive === true
                ? {
                    backgroundColor: 'var(--color-danger)',
                    color: 'var(--color-on-accent)',
                    borderColor: 'var(--color-danger)',
                  }
                : {
                    backgroundColor: 'var(--color-accent-blue)',
                    color: 'var(--color-on-accent)',
                    borderColor: 'var(--color-accent-blue)',
                  }
            }
          >
            {pending ? 'Saving…' : (prompt?.confirmLabel ?? 'Confirm')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
